import { useEffect, useState } from "react";
import axios from "axios";
import config from "./config";
import PageLoader from "./PageLoader";
import { readTheme, writeTheme } from "./communityTheme";
import applyCommunityFont, { applyCommunityDirection } from "./applyFont";
import { pickGroupTemplate } from "./group-templates";

// Default marketing content shown when a group hasn't set up its landing page
// (mirrors guruai's $defaultSkills fallback in landing_page_preview.blade.php).
const DEFAULT_SKILL_HEADING = "Skills you will walk away with";
const DEFAULT_SKILLS = [
  "Exclusive trainings and insider knowledge",
  "High-value resources and downloadable guides",
  "Proven frameworks and strategies for real results",
  "Step-by-step learning content you can apply immediately",
  "Group discussions and support",
  "Access to special workshops, masterclasses, and events",
  "Tools and resources to accelerate your progress",
  "Expert insights and real-world strategies",
  "Continuous updates and new learning opportunities",
];

/**
 * Public group landing page — /:token?gid={group_token}
 * Shows the group's description, media gallery and skills (marketing page),
 * mirroring guruai's landing page preview.
 */
export default function GroupLanding({ token, gid, member, onBack, onJoin, onLogin, onInstructor, embedded = false, referral = null, myGroups = [], onSwitchGroup, onDiscoverGroups }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let on = true;
    setLoading(true);
    setError("");
    axios
      .post(`${config.COMMUNITY_API_URL}/community-group-landing`, {
        community_token: token,
        group_token: gid,
        community_member_id: member?.community_member_id || undefined,
        // Needed for the backend to verify membership (joined => referral link
        // instead of the Join button on the in-feed About tab).
        member_token: member?.auth_token,
      })
      .then((res) => {
        if (!on) return;
        if (res.data?.error === 0) {
          setData(res.data);
          writeTheme(token, res.data.community?.primary_color);
          applyCommunityFont(res.data.community?.membership_font);
          applyCommunityDirection(res.data.community?.rtl);
        } else {
          setError(res.data?.msg || "Landing page not found.");
        }
      })
      .catch(() => on && setError("Something went wrong."))
      .finally(() => on && setLoading(false));
    return () => {
      on = false;
    };
  }, [token, gid, member]);

  if (loading) {
    return (
      <PageLoader embedded={embedded} primary={data?.community?.primary_color || readTheme(token) || "#0274F7"} />
    );
  }
  if (error || !data) {
    return (
      <div className={`${embedded ? "py-20" : "min-h-screen"} flex flex-col items-center justify-center gap-3 text-gray-500`}>
        <p>{error || "Not found"}</p>
        <button onClick={onBack} className="text-sm font-medium text-gray-700 underline">
          Back to community
        </button>
      </div>
    );
  }

  const { community, group, landing } = data;
  const primary = community.primary_color || "#0274F7";
  const media = landing.media || [];
  // Access type: 1 = Public, 2 = Private. Pricing is separate.
  const accessType = Number(group.access_type);
  const isPrivate = accessType === 2;
  const pricing = group.pricing_type || "free";
  const isPaid = pricing === "paid";
  const isTrial = pricing === "free_trial";
  const accessLabel = isPrivate ? "Private" : "Public";
  const priceLabel = group.price_label || "";
  // Bare amount only (strip the "Free trial · " prefix) for the left meta.
  const amountLabel = priceLabel.replace(/^Free trial · /, "");

  // Default image: backend already resolves group.banner to the default banner
  // when the group has none, so use it both for an empty gallery and as the
  // onError fallback for any broken media URL.
  const defaultImage = group.banner;

  // Default content: when the group hasn't added its own skills, fall back to
  // the default checklist (mirrors guruai's landing page preview).
  const rawSkills = ((landing.skill?.list?.length ? landing.skill.list : landing.skill_list) || [])
    .filter((s) => s && String(s).trim());
  const skillList = rawSkills.length ? rawSkills : DEFAULT_SKILLS;
  const skillHeading =
    (landing.skill?.heading || landing.skill_heading || "").trim() || DEFAULT_SKILL_HEADING;

  const description = (group.description || "").trim();
  const shortDescription =
    description.length > 500 ? `${description.slice(0, 500)}...` : description;
  // Free-trial members whose trial has lapsed must subscribe to keep access.
  const trialLapsed = isTrial && group.joined && group.trial_expired;
  const joinLabel = isPaid
    ? // Paid groups require payment — always show "Get access" (no free entry).
      priceLabel
      ? `Get access — ${priceLabel}`
      : "Get access"
    : trialLapsed
    ? priceLabel
      ? `Buy now — ${priceLabel}`
      : "Buy now"
    : group.joined
    ? "Enter group"
    : isTrial
    ? group.trial_duration
      ? `Start ${group.trial_duration}-day free trial`
      : "Start free trial"
    : isPrivate
    ? "Request to join"
    : "Join Group";
  const postCount = group.post_count ?? 0;
  // The third sidebar stat is labelled "Courses" — show the real course count.
  const courseStat = group.course_count ?? 0;

  const Template = pickGroupTemplate(landing.template);
  return (
    <Template
      community={community}
      group={group}
      landing={landing}
      primary={primary}
      media={media}
      defaultImage={defaultImage}
      accessLabel={accessLabel}
      isPrivate={isPrivate}
      pricing={pricing}
      isPaid={isPaid}
      isTrial={isTrial}
      amountLabel={amountLabel}
      priceLabel={priceLabel}
      joinLabel={joinLabel}
      skillHeading={skillHeading}
      skillList={skillList}
      shortDescription={shortDescription}
      postCount={postCount}
      courseStat={courseStat}
      member={member}
      embedded={embedded}
      referral={referral}
      token={token}
      gid={gid}
      myGroups={myGroups}
      onJoin={onJoin}
      onLogin={onLogin}
      onInstructor={onInstructor}
      onSwitchGroup={onSwitchGroup}
      onDiscoverGroups={onDiscoverGroups}
    />
  );
}
